import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
type RoleSelectionProps = {
  onSelectRole: (role: 'user' | 'admin') => void;
};
export const RoleSelection: React.FC<RoleSelectionProps> = ({
  onSelectRole
}) => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'user' | 'admin' | null>(null);
  const handleRoleSelect = (role: 'user' | 'admin') => {
    setSelectedRole(role);
    setTimeout(() => onSelectRole(role), 500); // Delay to allow for animation
  };
  return <div className="relative min-h-screen w-full bg-gradient-to-b from-[#0A1128] to-[#1C2541] text-white overflow-hidden" data-id="element-825">
      {/* Animated background with stars and icons */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-826">
        <div className="absolute top-0 left-0 w-full h-full opacity-30" data-id="element-827">
          {/* Stars background */}
          {[...Array(100)].map((_, i) => <motion.div key={i} className="absolute rounded-full bg-white" initial={{
          opacity: 0.2
        }} animate={{
          opacity: [0.2, 0.8, 0.2],
          scale: [1, 1.2, 1]
        }} transition={{
          duration: Math.random() * 5 + 5,
          repeat: Infinity,
          delay: Math.random() * 5
        }} style={{
          width: Math.random() * 3 + 1 + 'px',
          height: Math.random() * 3 + 1 + 'px',
          top: Math.random() * 100 + '%',
          left: Math.random() * 100 + '%'
        }} data-id="element-828" />)}
          {/* Floating career icons */}
          {[{
          icon: '📚',
          top: '15%',
          left: '20%',
          delay: 0
        }, {
          icon: '💻',
          top: '25%',
          left: '75%',
          delay: 0.5
        }, {
          icon: '🌎',
          top: '65%',
          left: '30%',
          delay: 1
        }, {
          icon: '🔬',
          top: '45%',
          left: '85%',
          delay: 1.5
        }, {
          icon: '🎨',
          top: '75%',
          left: '60%',
          delay: 2
        }].map((item, index) => <motion.div key={index} className="absolute text-2xl md:text-4xl" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: item.delay,
          duration: 1
        }} style={{
          top: item.top,
          left: item.left
        }} data-id="element-829">
              {item.icon}
            </motion.div>)}
        </div>
      </div>
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen p-4" data-id="element-830">
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="mb-8 text-center" data-id="element-831">
          <img src="https://images.unsplash.com/photo-1506318137071-a8e063b4bec0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80" alt="CareerAstro Logo" className="w-24 h-24 mx-auto rounded-full bg-[#7D56F4] p-2 mb-4" data-id="element-832" />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-[#7D56F4] to-[#FF8F5E] text-transparent bg-clip-text" data-id="element-833">
            CareerAstro
          </h1>
          <p className="mt-2 text-lg text-[#E1E6FF]" data-id="element-834">
            Where Science Meets Destiny
          </p>
        </motion.div>
        <div className="w-full max-w-md space-y-6" data-id="element-835">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="space-y-4" data-id="element-836">
            <button onClick={() => handleRoleSelect('user')} className="w-full bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-semibold py-4 px-6 rounded-lg flex items-center justify-between hover:shadow-lg transition-all" data-id="element-837">
              <div className="flex items-center" data-id="element-838">
                <div className="bg-white/20 rounded-full p-2 mr-4" data-id="element-839">
                  <span className="text-xl" data-id="element-840">✨</span>
                </div>
                <span data-id="element-841">I'm a Career Explorer</span>
              </div>
              <ArrowRightIcon size={20} data-id="element-842" />
            </button>
            <button onClick={() => handleRoleSelect('admin')} className="w-full bg-gradient-to-r from-[#3A506B] to-[#5C6B7A] text-white font-semibold py-4 px-6 rounded-lg flex items-center justify-between hover:shadow-lg transition-all" data-id="element-843">
              <div className="flex items-center" data-id="element-844">
                <div className="bg-white/20 rounded-full p-2 mr-4" data-id="element-845">
                  <span className="text-xl" data-id="element-846">👨‍💼</span>
                </div>
                <span data-id="element-847">I'm an Admin/Coach</span>
              </div>
              <ArrowRightIcon size={20} data-id="element-848" />
            </button>
          </motion.div>
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.6
        }} className="flex justify-center space-x-4 pt-4" data-id="element-849">
            <button onClick={() => setIsLoginModalOpen(true)} className="text-[#B8C0E0] hover:text-white transition-colors" data-id="element-850">
              Log In
            </button>
            <span className="text-[#3A506B]" data-id="element-851">|</span>
            <button onClick={() => setIsSignupModalOpen(true)} className="text-[#B8C0E0] hover:text-white transition-colors" data-id="element-852">
              Sign Up
            </button>
          </motion.div>
        </div>
      </div>
      {/* Login Modal */}
      {isLoginModalOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-id="element-853">
          <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="bg-[#1C2541] rounded-lg p-6 w-full max-w-md" data-id="element-854">
            <h2 className="text-2xl font-bold mb-6 text-center" data-id="element-855">
              Welcome Back
            </h2>
            <div className="space-y-4" data-id="element-856">
              <div data-id="element-857">
                <label className="block text-sm font-medium text-[#B8C0E0] mb-1" data-id="element-858">
                  Email
                </label>
                <input type="email" className="w-full rounded-lg bg-[#0A1128] border border-[#3A506B] p-3 text-white" data-id="element-859" />
              </div>
              <div data-id="element-860">
                <label className="block text-sm font-medium text-[#B8C0E0] mb-1" data-id="element-861">
                  Password
                </label>
                <input type="password" className="w-full rounded-lg bg-[#0A1128] border border-[#3A506B] p-3 text-white" data-id="element-862" />
              </div>
              <div className="flex justify-between items-center text-sm" data-id="element-863">
                <label className="flex items-center" data-id="element-864">
                  <input type="checkbox" className="mr-2" data-id="element-865" />
                  <span className="text-[#B8C0E0]" data-id="element-866">Remember me</span>
                </label>
                <a href="#" className="text-[#7D56F4]" data-id="element-867">
                  Forgot password?
                </a>
              </div>
              <button className="w-full bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-semibold py-3 px-4 rounded-lg" data-id="element-868">
                Log In
              </button>
              <div className="flex justify-center mt-4" data-id="element-869">
                <button onClick={() => setIsLoginModalOpen(false)} className="text-[#B8C0E0] hover:text-white" data-id="element-870">
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>}
      {/* Sign Up Modal */}
      {isSignupModalOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-id="element-871">
          <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="bg-[#1C2541] rounded-lg p-6 w-full max-w-md" data-id="element-872">
            <h2 className="text-2xl font-bold mb-6 text-center" data-id="element-873">
              Create Account
            </h2>
            <div className="space-y-4" data-id="element-874">
              <div data-id="element-875">
                <label className="block text-sm font-medium text-[#B8C0E0] mb-1" data-id="element-876">
                  Full Name
                </label>
                <input type="text" className="w-full rounded-lg bg-[#0A1128] border border-[#3A506B] p-3 text-white" data-id="element-877" />
              </div>
              <div data-id="element-878">
                <label className="block text-sm font-medium text-[#B8C0E0] mb-1" data-id="element-879">
                  Email
                </label>
                <input type="email" className="w-full rounded-lg bg-[#0A1128] border border-[#3A506B] p-3 text-white" data-id="element-880" />
              </div>
              <div data-id="element-881">
                <label className="block text-sm font-medium text-[#B8C0E0] mb-1" data-id="element-882">
                  Password
                </label>
                <input type="password" className="w-full rounded-lg bg-[#0A1128] border border-[#3A506B] p-3 text-white" data-id="element-883" />
              </div>
              <div className="flex items-center text-sm" data-id="element-884">
                <input type="checkbox" className="mr-2" data-id="element-885" />
                <span className="text-[#B8C0E0]" data-id="element-886">
                  I agree to the Terms and Privacy Policy
                </span>
              </div>
              <button className="w-full bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-semibold py-3 px-4 rounded-lg" data-id="element-887">
                Sign Up
              </button>
              <div className="flex justify-center mt-4" data-id="element-888">
                <button onClick={() => setIsSignupModalOpen(false)} className="text-[#B8C0E0] hover:text-white" data-id="element-889">
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </div>;
};